/**
 * Live webcam capture used by the sign-in page. Opens the camera, loads the
 * face models, and lets the user capture a single frame's face descriptor.
 *
 * It never uploads images — `onCapture` receives only the 128-number
 * descriptor (or the caller falls back to password-only via `onCancel`).
 */

import { useEffect, useRef, useState } from "react";

import { descriptorFromVideo, loadFaceModels } from "./face";

interface Props {
  /** Heading shown above the camera, e.g. "Verify your face". */
  title: string;
  /** Called with the captured descriptor. May be async (e.g. a server verify);
   *  the capture button stays busy until it resolves. */
  onCapture: (descriptor: Float32Array) => void | Promise<void>;
  /** Called when the user backs out (or the camera is unavailable). */
  onCancel: (reason?: string) => void;
  /** Label for the cancel/fallback control. */
  cancelLabel?: string;
}

export function FaceScan({ title, onCapture, onCancel, cancelLabel = "Use password only" }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState("Starting camera…");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Kick off the (larger) model download and the camera in parallel.
        const modelsP = loadFaceModels();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: 320, height: 240 },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStatus("Loading face models…");
        await modelsP;
        if (cancelled) return;
        setReady(true);
        setStatus("Center your face in the frame, then capture.");
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof DOMException && err.name === "NotAllowedError"
            ? "Camera permission denied."
            : "Camera or face models unavailable.";
        onCancel(msg);
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // onCancel is stable enough for this one-shot setup; intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function capture() {
    if (!videoRef.current || !ready || working) return;
    setWorking(true);
    setStatus("Reading face…");
    try {
      const descriptor = await descriptorFromVideo(videoRef.current);
      if (!descriptor) {
        setStatus("No single face detected — adjust and try again.");
        setWorking(false);
        return;
      }
      await onCapture(descriptor);
      // If the caller kept us mounted (e.g. a verify mismatch), re-enable so
      // the user can capture again. On success the parent unmounts us instead.
      if (mountedRef.current) {
        setWorking(false);
        setStatus("Center your face in the frame, then capture.");
      }
    } catch {
      setStatus("Could not read face — try again.");
      setWorking(false);
    }
  }

  return (
    <div className="face-scan">
      <h2 className="face-scan-title">{title}</h2>
      <div className="face-scan-frame">
        {/* Mirror the preview so it feels like a selfie camera. */}
        <video ref={videoRef} muted playsInline className="face-scan-video" />
      </div>
      <p className="face-scan-status">{status}</p>
      <button type="button" className="face-scan-capture" onClick={capture} disabled={!ready || working}>
        {working ? "Working…" : "Capture"}
      </button>
      <button type="button" className="face-scan-cancel" onClick={() => onCancel()}>
        {cancelLabel}
      </button>
    </div>
  );
}
